import {isFeatureEnabled} from 'feature-flags';
import {Card, Form} from 'platform/components';
import {Box, Hide, HStack, Integer, Show, VStack} from 'platform/foundation';

import {useCallback, useMemo} from 'react';
import {Outlet} from 'react-router-dom';

import {isNil, isNotNil} from 'ramda';

import {suffixTestId, TestIdProps, useDebouncedCallback, useQueryState} from 'shared/web';

import {FilterContextType, FilterProvider} from '../../hooks/FilterContext';
import {useSelectedFilters} from '../../hooks/SelectedFiltersContext';
import {useHeaderHeight} from '../../hooks/useHeaderHeight';
import {usePriceReport} from '../../hooks/usePriceReport';
import {usePriceReportSearchParams} from '../../hooks/usePriceReportSearchParams';
import {FilterFormData} from '../../types/FilterFormData';
import {featureFlags} from '../../utils/featureFlags';
import {getDefaultValues} from '../../utils/getDefaultValues';
import {QueryStateKeys} from '../../utils/routing';
import {validateFilterValues} from '../../utils/validateFilterValues';
import {SavedFilters} from '../Filter/components/SavedFilters/SavedFilters';
import {Filter} from '../Filter/Filter';
import {formValuesToRequestValues} from '../Filter/utils/formValuesToRequestValues';
import {StickyContainer} from '../StickyContainer/StickyContainer';
import {FilterModal} from './components/FilterModal';

export function FilterLayout(props: TestIdProps) {
  const {priceReport, requestData} = usePriceReport();
  const {selectedFilters, onSelectFilters} = useSelectedFilters();
  const headerHeight = useHeaderHeight();
  const {resetPage} = usePriceReportSearchParams();
  const [, , clearDebug] = useQueryState(QueryStateKeys.Debug);

  const changeHandler = useCallback(
    (context: FilterContextType, values: FilterFormData) => {
      const errors = validateFilterValues(values);
      context.setErrors(errors);

      if (isNil(errors)) {
        context.onFilterChange(formValuesToRequestValues(values));
        onSelectFilters(values);
        resetPage();
      }
    },
    [resetPage, onSelectFilters]
  );

  const debouncedChangeHandler = useDebouncedCallback(changeHandler, 500);

  const defaultValues = useMemo(() => getDefaultValues(priceReport), [priceReport]);

  const showSavedFilters =
    isFeatureEnabled(featureFlags.PR_RELATIVE_FILTERS) && isNotNil(requestData?.customerId);

  if (!priceReport) {
    return null;
  }

  return (
    <FilterProvider initialFilter={formValuesToRequestValues(selectedFilters ?? defaultValues)}>
      {(context) => (
        <Form<FilterFormData>
          defaultValues={selectedFilters ?? defaultValues}
          onChange={(values) => {
            debouncedChangeHandler(context, values);
          }}
        >
          {(control, formApi) => {
            const onReset = () => {
              formApi.reset(defaultValues);
              context.onFilterChange(formValuesToRequestValues(defaultValues));
              context.setErrors(null);
              onSelectFilters(null);
              clearDebug();
            };

            return (
              <>
                <HStack>
                  <Hide onMobile onTablet>
                    <Box width={80} flexShrink={0} padding={2} paddingRight={0}>
                      <StickyContainer offset={2}>
                        <VStack spacing={3}>
                          <Show when={showSavedFilters}>
                            <Card>
                              <SavedFilters
                                priceReport={priceReport}
                                formApi={formApi}
                                onFilterChange={() => control.onChange?.()}
                                data-testid={suffixTestId('savedFilters', props)}
                              />
                            </Card>
                          </Show>
                          <Card>
                            <Filter
                              priceReport={priceReport}
                              formControl={control}
                              formApi={formApi}
                              onReset={onReset}
                              isSavedFiltersExternal={showSavedFilters}
                              data-testid="filter"
                            />
                          </Card>
                        </VStack>
                      </StickyContainer>
                    </Box>
                  </Hide>
                  <Box flexGrow={1} minWidth={0}>
                    <Box
                      position={[undefined, undefined, 'sticky']}
                      top={(headerHeight / 4) as Integer}
                      data-testid={suffixTestId('stickyContainer', props)}
                    >
                      <Outlet />
                    </Box>
                  </Box>
                </HStack>
                <Show onMobile onTablet>
                  <FilterModal
                    priceReport={priceReport}
                    formControl={control}
                    formApi={formApi}
                    onReset={onReset}
                    isSavedFiltersExternal={showSavedFilters}
                    data-testid="filterModal"
                  />
                </Show>
              </>
            );
          }}
        </Form>
      )}
    </FilterProvider>
  );
}
